import { Route, Routes } from '@angular/router';
import { authGuard } from './core/auth/auth.guard';
import { permissionGuard } from './core/auth/permission.guard';
import { Permission } from './core/auth/permission.constants';
import { landingGuard } from './core/navigation/landing.guard';

const administrationPermissions = [
  Permission.Users.Read,
  Permission.Roles.Read,
  Permission.Permissions.Read,
] as const;

const reportPermissions = [
  Permission.Quotations.Read,
  Permission.Itinerary.Read,
  Permission.Trips.Read,
  Permission.SupplierRequests.Read,
  Permission.SupplierOffers.Read,
  Permission.Costings.Read,
  Permission.ExchangeRates.Read,
] as const;

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then((module) => module.LoginComponent),
  },
  {
    path: 'forbidden',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/access-denied/access-denied.component').then(
        (module) => module.AccessDeniedComponent,
      ),
  },
  {
    path: 'no-access',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/no-access/no-access.component').then((module) => module.NoAccessComponent),
  },
  { path: 'access-denied', redirectTo: 'forbidden', pathMatch: 'full' },
  // The customer payment page. Deliberately outside the shell and outside
  // authGuard: a customer has no account, and the unguessable token in the URL
  // is the whole of their authorisation.
  // The printable trip programme: outside the shell so it prints as a page.
  {
    path: 'trips/:tripId/programme',
    canActivate: [authGuard, permissionGuard],
    data: { requiredPermission: Permission.Trips.Read },
    loadComponent: () =>
      import('./features/trips/trip-programme/trip-programme.component').then(
        (module) => module.TripProgrammeComponent,
      ),
  },
  {
    path: 'pay/:token',
    loadComponent: () =>
      import('./features/pay/pay.component').then((module) => module.PayComponent),
  },
  {
    path: '',
    pathMatch: 'full',
    canActivate: [landingGuard],
    loadComponent: () =>
      import('./features/landing/landing.component').then((module) => module.LandingComponent),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layout/application-shell/application-shell.component').then(
        (module) => module.ApplicationShellComponent,
      ),
    children: [
      {
        path: 'profile',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/profile/profile.component').then((module) => module.ProfileComponent),
      },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            (module) => module.DashboardComponent,
          ),
      },
      protectedRoute('customers', Permission.Customers.Read, () =>
        import('./features/customers/customers.component').then(
          (module) => module.CustomersComponent,
        ),
      ),
      protectedRoute('inquiries', Permission.Inquiries.Read, () =>
        import('./features/inquiries/inquiries.component').then(
          (module) => module.InquiriesComponent,
        ),
      ),
      protectedRoute('trips', Permission.Trips.Read, () =>
        import('./features/trips/trips.component').then((module) => module.TripsComponent),
      ),
      protectedRoute('trips/:tripId', Permission.Trips.Read, () =>
        import('./features/trips/trip-workspace/trip-workspace.component').then(
          (module) => module.TripWorkspaceComponent,
        ),
      ),
      protectedRoute('suppliers', Permission.Suppliers.Read, () =>
        import('./features/suppliers/suppliers.component').then(
          (module) => module.SuppliersComponent,
        ),
      ),
      protectedRoute('vehicle-rates', Permission.Suppliers.Read, () =>
        import('./features/rates/vehicle-rates/vehicle-rates.component').then(
          (module) => module.VehicleRatesComponent,
        ),
      ),
      protectedRoute('activities', Permission.Activities.Read, () =>
        import('./features/activities/activities.component').then(
          (module) => module.ActivitiesComponent,
        ),
      ),
      protectedRoute('quotations/approvals', Permission.Quotations.Approve, () =>
        import('./features/quotations/manager-approvals.component').then(
          (module) => module.ManagerApprovalsComponent,
        ),
      ),
      protectedRoute('quotations', Permission.Quotations.Read, () =>
        import('./features/quotations/quotations.component').then(
          (module) => module.QuotationsComponent,
        ),
      ),
      // Bookings and operations are frontend-only for now: there is no
      // booking API, so each is guarded by the permission of the stage it
      // follows rather than by a booking permission that does not exist yet.
      protectedRoute('bookings', Permission.Quotations.Read, () =>
        import('./features/bookings/bookings.component').then((module) => module.BookingsComponent),
      ),
      protectedRoute('operations/tasks', Permission.Trips.Read, () =>
        import('./features/operations/tasks/tasks.component').then(
          (module) => module.OperationsTasksComponent,
        ),
      ),
      protectedRoute('operations/documents', Permission.Trips.Read, () =>
        import('./features/operations/documents/documents.component').then(
          (module) => module.OperationsDocumentsComponent,
        ),
      ),
      protectedRoute('operations/communications', Permission.Trips.Read, () =>
        import('./features/operations/communications/communications.component').then(
          (module) => module.OperationsCommunicationsComponent,
        ),
      ),
      protectedRoute('operations', Permission.Trips.Read, () =>
        import('./features/operations/operations.component').then(
          (module) => module.OperationsComponent,
        ),
      ),
      // Finance is frontend-only too, and follows the quotation it bills for,
      // so it is guarded by the same permission until a finance API exists.
      protectedRoute('finance/invoices', Permission.Quotations.Read, () =>
        import('./features/finance/invoices/invoices.component').then(
          (module) => module.InvoicesComponent,
        ),
      ),
      protectedRoute('finance/payments', Permission.Quotations.Read, () =>
        import('./features/finance/payments/payments.component').then(
          (module) => module.PaymentsComponent,
        ),
      ),
      // Supplier settlement is internal and follows the booking's costing, so
      // it is guarded by the costing permission until a settlement API exists.
      protectedRoute('finance/supplier-payments', Permission.Costings.Read, () =>
        import('./features/settlements/settlements.component').then(
          (module) => module.SettlementsComponent,
        ),
      ),
      {
        path: 'finance',
        redirectTo: 'finance/invoices',
        pathMatch: 'full',
      },
      protectedRoute('exchange-rates', Permission.ExchangeRates.Read, () =>
        import('./features/exchange-rates/exchange-rates.component').then(
          (module) => module.ExchangeRatesComponent,
        ),
      ),
      {
        path: 'reports',
        canActivate: [permissionGuard],
        data: { anyPermissions: reportPermissions },
        loadComponent: () =>
          import('./features/reports/reports.component').then((module) => module.ReportsComponent),
      },
      protectedRoute('administration/users', Permission.Users.Read, () =>
        import('./features/administration/users/users-page/users-page.component').then(
          (module) => module.UsersPageComponent,
        ),
      ),
      protectedRoute('administration/roles', Permission.Roles.Read, () =>
        import('./features/administration/roles/roles-page/roles-page.component').then(
          (module) => module.RolesPageComponent,
        ),
      ),
      protectedRoute('administration/company/settings', Permission.Company.Read, () =>
        import('./features/administration/company/company-settings-page.component').then(
          (module) => module.CompanySettingsPageComponent,
        ),
      ),
      protectedRoute('administration/company', Permission.Company.Read, () =>
        import('./features/administration/company/company-page.component').then(
          (module) => module.CompanyPageComponent,
        ),
      ),
      {
        path: 'administration',
        canActivate: [permissionGuard],
        data: { anyPermissions: administrationPermissions },
        loadComponent: () =>
          import('./features/administration/administration.component').then(
            (module) => module.AdministrationComponent,
          ),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];

function protectedRoute(
  path: string,
  requiredPermission: string,
  loadComponent: NonNullable<Route['loadComponent']>,
): Route {
  return {
    path,
    canActivate: [permissionGuard],
    data: { requiredPermission },
    loadComponent,
  };
}
